import type { Node } from '@xyflow/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFamilyTree } from '../api';
import type { FamilyTreeEdgeType, FamilyTreeNode, PartnerNodeData } from '../interfaces';
import { isNodeIdInEdge } from '../utils';

interface IUseFamilyTreeNodesEdgesResult {
    nodes: FamilyTreeNode[];
    edges: FamilyTreeEdgeType[];
    isLoadingFamilyTree: boolean;
    noRelationsNode?: FamilyTreeNode;
    hasRelations: boolean;
}

export function useFamilyTreeNodesEdges(partnerUuid?: string, isPresentationMode = false, isPrintMode = false): IUseFamilyTreeNodesEdgesResult {
    const { t } = useTranslation();
    const { data: familyTree, isLoading: isLoadingFamilyTree } = useFamilyTree(partnerUuid);

    const edges = useMemo<FamilyTreeEdgeType[]>(
        () =>
            familyTree?.relationships.length
                ? familyTree.relationships.map((relationship) => ({
                      id: relationship.relationshipUuid,
                      source: relationship.source,
                      target: relationship.target,
                      type: 'familyTreeEdge',
                      data: { relationshipType: relationship.relationshipType, justification: relationship.justification ?? '' },
                  }))
                : [],
        [familyTree],
    );

    const nodes = useMemo<FamilyTreeNode[]>(
        () =>
            [
                ...(familyTree?.partnerNodes.map((node) => ({
                    id: node.uuid,
                    type: 'familyTreeNode',
                    position: { x: 0, y: 0 },
                    data: {
                        ...node,
                        usedHandles: [],
                        isMainPartner: node.uuid === partnerUuid,
                        isConnector: false,
                        isPresentationMode,
                        isPrintMode,
                    },
                })) ?? []),
                ...(familyTree?.connectorNodes.map((node) => ({
                    id: node.uuid,
                    type: 'familyTreeConnectorNode',
                    position: { x: 0, y: 0 },
                    data: {
                        label: t(`familyTree-${node.relationshipType}`),
                        usedHandles: [],
                        isConnector: true,
                        relationshipType: node.relationshipType,
                        nodePosition: node.nodePosition,
                        visibility: node.visibility,
                    },
                })) ?? []),
            ].filter((node: FamilyTreeNode) => edges.some(isNodeIdInEdge(node.id))),
        [familyTree?.connectorNodes, familyTree?.partnerNodes, edges, partnerUuid, isPresentationMode, isPrintMode, t],
    );

    const hasRelations = useMemo<boolean>(() => !!familyTree && familyTree.partnerNodes.length > 1 && !!familyTree.relationships.length, [familyTree]);

    const noRelationsNode = useMemo<Node<PartnerNodeData> | undefined>(
        () =>
            familyTree && !hasRelations
                ? {
                      id: familyTree.partnerNodes[0].uuid,
                      type: 'familyTreeNoRelationsNode',
                      position: { x: 200, y: 0 },
                      data: { ...familyTree!.partnerNodes[0], usedHandles: [], isMainPartner: true, isConnector: false, isPresentationMode, isPrintMode },
                  }
                : undefined,
        [familyTree, hasRelations, isPresentationMode, isPrintMode],
    );

    return { nodes, edges, isLoadingFamilyTree, noRelationsNode, hasRelations };
}
